import { Dialog } from "primereact/dialog";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import "./UpdateItemStatus.scss";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { ReactComponent as ExportNotesIcon } from "src/assets/icons/export_notes.svg";
import { ALLOWED_FILES_EXTENSION } from "src/resources/constant";
import { getFieldName, isNullOrWhiteSpace } from "src/utils/helpers";
import { useState } from "react";
import {
  setIsUpdateStatusPopupOpen,
  save,
  setPreSelectedStatus,
  setOnHoldReason,
} from "src/redux/slices/sharedSlice";
import FileUploader from "../FileUploader/FileUploader";
import {
  PopupType,
  PreventedAppointmentStatusesByUser,
  StatusesWithHoldReason,
} from "src/resources/enum";
import { updateStatusInitialState } from "src/state/updateStatusInitialState";
import { FileUpload } from "primereact/fileupload";
import {
  initializeSendRequestToOwnerPopupData,
  sendRequestToOwner,
} from "src/redux/slices/bMCProfileSlice";

const UpdateItemStatusPopup = ({
  successCallbackFn,
}: {
  successCallbackFn?: Function;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common"]);

  const fileUploadRef = useRef<FileUpload>(null);

  const { isUpdateStatusPopupOpen, statusId, popupType, onHoldReasonID } =
    useSelector((state: any) => state.shared);

  const statuses = useSelector((state: any) => {
    if (popupType === PopupType.Appointment) {
      return state.preventiveAppointments.appointmentStatusForEdit;
    } else if (popupType === PopupType.ATECase) {
      return state.ateCases.caseStatusForEdit;
    } else {
      return state.maintenanceRequests.maintenanceStatusForEdit;
    }
  });

  const status = statuses?.find((x: { id: number }) => x.id == statusId);

  const [onHoldReasonVisible, setOnHoldReasonVisible] = useState(
    status?.englishName?.toLowerCase() !== "on hold" ? false : true
  );
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [statusDetails, setStatusDetails] = useState(updateStatusInitialState);

  const { sendRequestToOwnerData } = useSelector(
    (state: any) => state.bMCProfile.sendRequestToOwnerPopup
  );

  const { language, onHoldReasons } = useSelector((state: any) => state.lookup);

  const handleFormFieldChange = (e: any) => {
    let name: any, value: any;
    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }

    setStatusDetails({ ...statusDetails, [name]: value });
    if (name === "onHoldReasonID") dispatch(setOnHoldReason(value));
    else dispatch(setOnHoldReason(statusDetails.onHoldReasonID));
  };

  const handleFileChange = (event: any) => {
    const file = event.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop();
      if (ALLOWED_FILES_EXTENSION.includes(fileExtension.toLowerCase())) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const fileExtension = file.name.split(".").pop();
          const fileObject = {
            fileName: file.name,
            fileExtension,
            fileBase64: reader.result as string,
            subject: "update status",
          };
          setStatusDetails({ ...statusDetails, maintenanceReport: fileObject });
        };
        reader.readAsDataURL(file);
      } else {
        toast.error(t("validFileExtension"));
        fileUploadRef.current?.clear(); // Clear the file if it doesn’t meet criteria
      }
    }
  };
  const handleSubmit = (event: any): void => {
    event.preventDefault();

    if (
      status?.englishName?.toLowerCase() ===
        StatusesWithHoldReason.PendingOwnerApproval &&
      !onHoldReasonID
    ) {
      toast.error(t("onHoldReasonRequired"));
      return;
    }
    dispatch(save(statusDetails, onSuccess) as any);
  };

  const handleSendRequestToOwner = (): void => {
    const payload = {
      ...sendRequestToOwnerData,
      typeOfApproval: statusDetails.onHoldReasonID,
      description: statusDetails.bmcFeedback,
    };
    dispatch(sendRequestToOwner(payload) as any);
  };

  const handleSetSelectedStatusId = (e: any): void => {
    const status = statuses?.find(
      (x: { id: number }) => x.id === e.target.value
    );
    if (status) {
      setStatusDetails({ ...statusDetails, onHoldReasonID: null });
      dispatch(setOnHoldReason(""));
      if (
        popupType === PopupType.Appointment &&
        status.englishName?.toLowerCase() ===
          PreventedAppointmentStatusesByUser.Inactive
      ) {
        toast.error(t("inactiveStatusValidation"));
        setStatusDetails({ ...statusDetails, status: 1 });
        return;
      }

      if (
        popupType === PopupType.Appointment &&
        status.englishName?.toLowerCase() ===
          PreventedAppointmentStatusesByUser.Unassigned
      ) {
        toast.error(t("unassignStatusValidation"));
        setStatusDetails({ ...statusDetails, status: 1 });
        return;
      }
      if (
        status.englishName?.toLowerCase() ===
        StatusesWithHoldReason.PendingOwnerApproval
      ) {
        //set onHold Reason as visible
        setOnHoldReasonVisible(true);
      } else if (
        status.englishName?.toLowerCase() === "completed" &&
        popupType !== PopupType.Appointment
      ) {
        if (popupType === PopupType.ATECase) {
          toast.error(t("ateCompletedValidation"));
          setStatusDetails({ ...statusDetails, status: 2 });
          return;
        } else setFileUploadVisible(true);
      } else {
        clearPopupData();
      }
    }
    dispatch(setPreSelectedStatus(e.target.value));
  };

  const closePopup = () => {
    dispatch(setIsUpdateStatusPopupOpen(false));
    dispatch(
      initializeSendRequestToOwnerPopupData({
        systemServiceId: "",
        serviceTerritoryId: "",
        propertyId: "",
        requestReferenceNumber: "",
        appointmentId: "",
        caseId: "",
      })
    );
    clearPopupData();
  };

  const clearPopupData = () => {
    setStatusDetails(updateStatusInitialState);
    setOnHoldReasonVisible(false);
    setFileUploadVisible(false);
  };

  const onSuccess = () => {
    toast.success(t("updateStatusMessage"));
    if (
      status?.englishName?.toLowerCase() ===
      StatusesWithHoldReason.PendingOwnerApproval
    ) {
      handleSendRequestToOwner();
    }
    closePopup();
    successCallbackFn?.();
  };
  return (
    <Dialog
      visible={isUpdateStatusPopupOpen}
      onHide={closePopup}
      header={t("updateCaseStatus")}
      headerClassName="popup-header"
      className="maintenance-requests-details-popup update-item-status-popup"
    >
      <div className="pt-3">
        <div className="form-group d-flex flex-column mb-3">
          <label htmlFor="status" className="required">
            {t("status")}
          </label>
          <Dropdown
            id="status"
            options={statuses}
            placeholder={t("status")}
            className="w-50"
            panelClassName="status-dropdown-panel"
            onChange={handleSetSelectedStatusId}
            value={statusId.toString()}
            optionValue={"id"}
            optionLabel={getFieldName(language)}
            name="status"
          />
        </div>
        {status?.englishName?.toLowerCase() ===
        StatusesWithHoldReason.PendingOwnerApproval ? (
          <div className="form-group d-flex flex-column mb-3">
            <label htmlFor="typeOfApproval" className="required">
              {t("typeOfApproval")}
            </label>
            <Dropdown
              id="typeOfApproval"
              name="onHoldReasonID"
              value={onHoldReasonID}
              optionValue={"id"}
              optionLabel={getFieldName(language)}
              onChange={handleFormFieldChange}
              options={onHoldReasons}
              placeholder={t("select")}
              className="w-50 mb-2"
              panelClassName="status-dropdown-panel"
            />
          </div>
        ) : null}

        {fileUploadVisible ? (
          <div>
            <label htmlFor="maintenanceReport">{t("evidenceDocument")}</label>
            <FileUploader
              fileUploadRef={fileUploadRef}
              id="maintenanceReport"
              name="maintenanceReport"
              accept=".pdf,.jpg,.jpeg"
              onChange={handleFileChange}
              fileName={statusDetails.maintenanceReport?.fileName || ""}
            />

            <p className="note">{t("fileSizeAndTypeNote")}</p>
          </div>
        ) : null}

        <div className="form-group d-flex flex-column">
          <label htmlFor="bmcFeedback" className="required">
            {t("statusReason")}
          </label>
          <InputTextarea
            id="bmcFeedback"
            name="bmcFeedback"
            placeholder={t("statusReasonPlaceholder")}
            value={statusDetails?.bmcFeedback}
            onChange={handleFormFieldChange}
          />
        </div>
        <div className="d-flex justify-content-end align-items-center gap-2 mt-4 send-request-to-owner-footer">
          <Button
            label={t("cancel")}
            className="light-primary-btn m-0"
            onClick={closePopup}
          />
          <Button
            label={t("updateCaseStatus")}
            className="edit-btn primary-btn m-0"
            icon={<ExportNotesIcon />}
            onClick={handleSubmit}
            disabled={isNullOrWhiteSpace(statusDetails?.bmcFeedback)}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default UpdateItemStatusPopup;
